import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import IndustryHero from './components/IndustryHero';
import IndustryCard from './components/IndustryCard';
import ROICalculator from './components/ROICalculator';
import CaseStudyCard from './components/CaseStudyCard';
import IndustryBenchmarks from './components/IndustryBenchmarks';
import IndustryDetailModal from './components/IndustryDetailModal';
import CTASection from './components/CTASection';
import type { Industry, IndustryBenchmark } from './types';
import Icon from '../../components/AppIcon';


const IndustrySolutions: React.FC = () => {
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const heroStats = [
  { label: 'Industries Served', value: '12+', icon: 'Building2' },
  { label: 'Average Time Saved', value: '60hrs/mo', icon: 'Clock' },
  { label: 'Client Satisfaction', value: '98%', icon: 'Star' }];


  const industries: Industry[] = [
  {
    id: 'ecommerce',
    name: 'E-Commerce',
    icon: 'ShoppingCart',
    description:
    'Automate order processing, inventory management, customer communications, and marketing campaigns to scale your online store efficiently.',
    image: "https://images.unsplash.com/photo-1549194388-2469d59ec75c",
    alt: 'Modern e-commerce warehouse with organized inventory shelves and automated sorting systems',
    challenges: [
    'Manual order processing consuming 20+ hours weekly',
    'Inventory sync issues across multiple platforms',
    'Delayed customer communication and support',
    'Time-consuming marketing campaign management',
    'Complex shipping and fulfillment workflows'],

    solutions: [
    'Automated order processing and fulfillment workflows',
    'Real-time inventory synchronization across all channels',
    'AI-powered customer support chatbots',
    'Automated email marketing and abandoned cart recovery',
    'Integrated shipping label generation and tracking'],

    metrics: {
      timeSaved: '60-70 hours',
      costReduction: '45-55%',
      efficiency: '80%'
    },
    caseStudies: [
    {
      id: 'cs-ecom-1',
      clientName: 'Fashion Forward Boutique',
      industry: 'E-Commerce',
      challenge:
      'Spending 25 hours weekly on manual order processing and inventory updates across Shopify, Amazon, and eBay.',
      solution:
      'Implemented automated order routing, inventory sync, and customer notification system using Make.com and n8n workflows.',
      results: [
      { metric: 'Time Saved', value: '68hrs/mo', description: 'Monthly hours reclaimed' },
      { metric: 'Order Speed', value: '3x faster', description: 'Processing time' },
      { metric: 'Accuracy', value: '99.8%', description: 'Order accuracy rate' }],

      testimonial: {
        quote:
        'The automation transformed our business. We went from drowning in orders to scaling effortlessly.',
        author: 'Sarah Mitchell',
        position: 'Owner, Fashion Forward Boutique',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_116022a91-1763299783599.png",
        alt: 'Professional woman with brown hair in business casual attire smiling at camera'
      },
      image: "https://images.unsplash.com/photo-1599948414997-92bdc8270434",
      alt: 'Happy female entrepreneur working on laptop in modern boutique store with clothing displays'
    }],

    tools: ['Shopify', 'WooCommerce', 'Make.com', 'n8n', 'WhatsApp API', 'Gmail', 'Sheets']
  },
  {
    id: 'real-estate',
    name: 'Real Estate',
    icon: 'Home',
    description:
    'Streamline lead management, property listings, client communications, and document workflows to close more deals faster.',
    image: "https://images.unsplash.com/photo-1712311082180-4fd73ded1b1c",
    alt: 'Modern luxury real estate property exterior with glass windows and landscaped garden',
    challenges: [
    'Manual lead follow-up resulting in lost opportunities',
    'Time-consuming property listing updates',
    'Scattered client communication across platforms',
    'Complex document signing and management',
    'Inefficient showing scheduling and coordination'],

    solutions: [
    'Automated lead nurturing and follow-up sequences',
    'Multi-platform property listing synchronization',
    'Centralized client communication hub with WhatsApp integration',
    'Digital document workflow with e-signature automation',
    'Smart scheduling system for property showings'],

    metrics: {
      timeSaved: '50-60 hours',
      costReduction: '40-50%',
      efficiency: '75%'
    },
    caseStudies: [
    {
      id: 'cs-re-1',
      clientName: 'Premier Properties Group',
      industry: 'Real Estate',
      challenge:
      'Agents spending 30+ hours weekly on manual lead follow-ups and property listing updates across multiple platforms.',
      solution:
      'Created automated lead nurturing system with WhatsApp integration and multi-platform listing sync using n8n workflows.',
      results: [
      { metric: 'Lead Response', value: '10x faster', description: 'Response time' },
      { metric: 'Conversions', value: '+45%', description: 'Lead to client rate' },
      { metric: 'Time Saved', value: '55hrs/mo', description: 'Per agent monthly' }],

      testimonial: {
        quote:
        'Our agents now focus on closing deals instead of administrative tasks. Game changer for our business.',
        author: 'Michael Chen',
        position: 'Managing Broker, Premier Properties',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd81bcbd-1763299899173.png",
        alt: 'Professional Asian man in navy suit with confident smile in modern office'
      },
      image: "https://images.unsplash.com/photo-1714647211979-4591651108c1",
      alt: 'Real estate agent showing property details on tablet to interested couple in bright modern home'
    }],

    tools: ['Zillow', 'Realtor.com', 'WhatsApp API', 'Gmail', 'DocuSign', 'Calendly', 'Sheets']
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    icon: 'Heart',
    description:
    'Automate appointment scheduling, patient communications, billing processes, and records management while maintaining HIPAA compliance.',
    image: "https://images.unsplash.com/photo-1733295016251-b6becd07a7b0",
    alt: 'Modern healthcare clinic reception area with clean white interior and digital check-in kiosks',
    challenges: [
    'Manual appointment scheduling and reminders',
    'Time-consuming patient intake and registration',
    'Complex insurance verification processes',
    'Scattered patient communication channels',
    'Inefficient billing and payment collection'],

    solutions: [
    'Automated appointment booking with SMS/email reminders',
    'Digital patient intake forms with auto-population',
    'Streamlined insurance verification workflows',
    'Centralized patient communication platform',
    'Automated billing and payment processing'],

    metrics: {
      timeSaved: '45-55 hours',
      costReduction: '35-45%',
      efficiency: '70%'
    },
    caseStudies: [
    {
      id: 'cs-hc-1',
      clientName: 'Wellness Medical Center',
      industry: 'Healthcare',
      challenge:
      'Staff spending 20+ hours weekly on appointment scheduling, reminders, and patient follow-ups manually.',
      solution:
      'Implemented automated scheduling system with SMS reminders and patient portal integration using HIPAA-compliant workflows.',
      results: [
      { metric: 'No-Shows', value: '-65%', description: 'Reduction in missed appointments' },
      { metric: 'Admin Time', value: '48hrs/mo', description: 'Staff hours saved' },
      { metric: 'Satisfaction', value: '96%', description: 'Patient satisfaction score' }],

      testimonial: {
        quote:
        'The automation allowed our staff to focus on patient care instead of administrative tasks. Remarkable improvement.',
        author: 'Dr. Emily Rodriguez',
        position: 'Medical Director, Wellness Medical',
        avatar: "https://images.unsplash.com/photo-1663664971647-59de765183c1",
        alt: 'Female doctor in white coat with stethoscope smiling warmly in medical office'
      },
      image: "https://images.unsplash.com/photo-1666886573531-48d2e3c2b684",
      alt: 'Healthcare professional using digital tablet to review patient information in modern clinic'
    }],

    tools: ['Calendly', 'WhatsApp API', 'Gmail', 'Sheets', 'Stripe', 'Twilio', 'n8n']
  },
  {
    id: 'professional-services',
    name: 'Professional Services',
    icon: 'Briefcase',
    description:
    'Optimize client onboarding, project management, invoicing, and reporting to deliver exceptional service efficiently.',
    image: "https://images.unsplash.com/photo-1731458769726-cef60c792665",
    alt: 'Professional business team collaborating around conference table with laptops and documents',
    challenges: [
    'Manual client onboarding consuming excessive time',
    'Scattered project tracking and communication',
    'Time-consuming invoice generation and follow-ups',
    'Complex reporting and analytics compilation',
    'Inefficient document management and sharing'],

    solutions: [
    'Automated client onboarding workflows with document collection',
    'Integrated project management with automated status updates',
    'Smart invoicing with automated payment reminders',
    'Real-time reporting dashboards with auto-generated insights',
    'Centralized document repository with version control'],

    metrics: {
      timeSaved: '55-65 hours',
      costReduction: '40-50%',
      efficiency: '75%'
    },
    caseStudies: [
    {
      id: 'cs-ps-1',
      clientName: 'Strategic Consulting Partners',
      industry: 'Professional Services',
      challenge:
      'Consultants spending 15+ hours weekly on client onboarding, invoicing, and report generation.',
      solution:
      'Created automated onboarding system with document collection, smart invoicing, and auto-generated client reports.',
      results: [
      { metric: 'Onboarding', value: '4x faster', description: 'Client setup time' },
      { metric: 'Collections', value: '+38%', description: 'Faster payment collection' },
      { metric: 'Capacity', value: '+50%', description: 'More clients per consultant' }],

      testimonial: {
        quote:
        'We doubled our client capacity without hiring additional staff. The ROI was immediate and substantial.',
        author: 'James Patterson',
        position: 'Managing Partner, Strategic Consulting',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14b136d25-1763296852732.png",
        alt: 'Professional businessman in gray suit with confident expression in modern office setting'
      },
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_137b2b3bf-1763299658888.png",
      alt: 'Business consultant presenting strategy on digital screen to engaged client team'
    }],

    tools: ['Make.com', 'n8n', 'Gmail', 'Sheets', 'Stripe', 'DocuSign', 'Slack']
  },
  {
    id: 'education',
    name: 'Education',
    icon: 'GraduationCap',
    description:
    'Automate student enrollment, course management, communications, and administrative tasks to focus on teaching excellence.',
    image: "https://images.unsplash.com/photo-1653565685072-adcf967db6b7",
    alt: 'Modern classroom with students using laptops and interactive digital learning tools',
    challenges: [
    'Manual student enrollment and registration processes',
    'Time-consuming course scheduling and updates',
    'Scattered parent-teacher communication',
    'Complex attendance tracking and reporting',
    'Inefficient assignment collection and grading'],

    solutions: [
    'Automated enrollment workflows with document verification',
    'Smart course scheduling with conflict detection',
    'Centralized communication platform for all stakeholders',
    'Digital attendance tracking with automated notifications',
    'Streamlined assignment submission and feedback system'],

    metrics: {
      timeSaved: '40-50 hours',
      costReduction: '30-40%',
      efficiency: '65%'
    },
    caseStudies: [
    {
      id: 'cs-ed-1',
      clientName: 'Bright Future Academy',
      industry: 'Education',
      challenge:
      'Administrative staff spending 25+ hours weekly on enrollment, scheduling, and parent communications.',
      solution:
      'Implemented automated enrollment system with WhatsApp parent notifications and digital attendance tracking.',
      results: [
      { metric: 'Admin Time', value: '42hrs/mo', description: 'Staff hours saved' },
      { metric: 'Engagement', value: '+55%', description: 'Parent communication rate' },
      { metric: 'Accuracy', value: '99.5%', description: 'Attendance tracking' }],

      testimonial: {
        quote:
        'The automation freed our staff to focus on student success. Parents love the instant communication.',
        author: 'Dr. Lisa Thompson',
        position: 'Principal, Bright Future Academy',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1978c955e-1763294317570.png",
        alt: 'Female educator with glasses and warm smile in professional attire at school entrance'
      },
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_17793b40d-1763294472553.png",
      alt: 'Teacher using digital tablet to engage with diverse group of students in modern classroom'
    }],

    tools: ['WhatsApp API', 'Gmail', 'Sheets', 'Calendly', 'n8n', 'Make.com', 'Zoom']
  },
  {
    id: 'hospitality',
    name: 'Hospitality',
    icon: 'Coffee',
    description:
    'Streamline reservations, guest communications, inventory management, and service delivery for exceptional experiences.',
    image: "https://images.unsplash.com/photo-1733709225757-be2d7bbd8714",
    alt: 'Luxury hotel lobby with modern design, comfortable seating, and professional reception desk',
    challenges: [
    'Manual reservation management across platforms',
    'Delayed guest communication and requests',
    'Complex inventory and supply chain coordination',
    'Time-consuming staff scheduling and coordination',
    'Inefficient feedback collection and response'],

    solutions: [
    'Automated reservation sync across all booking platforms',
    'Instant guest communication via WhatsApp and SMS',
    'Smart inventory management with auto-reordering',
    'Intelligent staff scheduling based on occupancy',
    'Automated feedback collection with sentiment analysis'],

    metrics: {
      timeSaved: '50-60 hours',
      costReduction: '35-45%',
      efficiency: '70%'
    },
    caseStudies: [
    {
      id: 'cs-hosp-1',
      clientName: 'Seaside Resort & Spa',
      industry: 'Hospitality',
      challenge:
      'Staff managing reservations across 5 platforms manually, resulting in double bookings and guest dissatisfaction.',
      solution:
      'Created unified reservation system with automated guest communications and real-time availability sync.',
      results: [
      { metric: 'Bookings', value: '+42%', description: 'Increase in reservations' },
      { metric: 'Errors', value: '-95%', description: 'Reduction in double bookings' },
      { metric: 'Reviews', value: '4.8/5', description: 'Average guest rating' }],

      testimonial: {
        quote:
        'Zero double bookings and instant guest communication transformed our operations. Guests notice the difference.',
        author: 'Carlos Martinez',
        position: 'General Manager, Seaside Resort',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13f3594a5-1763299326661.png",
        alt: 'Professional hotel manager in formal attire with welcoming smile in elegant lobby'
      },
      image: "https://images.unsplash.com/photo-1600671012016-e5890d87f804",
      alt: 'Luxury hotel room with ocean view, modern amenities, and pristine housekeeping'
    }],

    tools: ['Booking.com', 'Airbnb', 'WhatsApp API', 'Gmail', 'Sheets', 'n8n', 'Make.com']
  }];


  const benchmarks: IndustryBenchmark[] = [
  {
    industry: 'E-Commerce',
    avgTimeSaved: '65hrs/mo',
    avgCostReduction: '50%',
    commonAutomations: [
    'Order processing automation',
    'Inventory synchronization',
    'Customer support chatbots',
    'Marketing campaign automation'],

    adoptionRate: '78%'
  },
  {
    industry: 'Real Estate',
    avgTimeSaved: '55hrs/mo',
    avgCostReduction: '45%',
    commonAutomations: [
    'Lead nurturing workflows',
    'Property listing sync',
    'Client communication automation',
    'Document signing workflows'],

    adoptionRate: '65%'
  },
  {
    industry: 'Healthcare',
    avgTimeSaved: '50hrs/mo',
    avgCostReduction: '40%',
    commonAutomations: [
    'Appointment scheduling',
    'Patient reminders',
    'Billing automation',
    'Records management'],

    adoptionRate: '72%'
  },
  {
    industry: 'Professional Services',
    avgTimeSaved: '60hrs/mo',
    avgCostReduction: '45%',
    commonAutomations: [
    'Client onboarding',
    'Invoice generation',
    'Report automation',
    'Project tracking'],

    adoptionRate: '68%'
  },
  {
    industry: 'Education',
    avgTimeSaved: '45hrs/mo',
    avgCostReduction: '35%',
    commonAutomations: [
    'Enrollment workflows',
    'Parent communications',
    'Attendance tracking',
    'Assignment management'],

    adoptionRate: '58%'
  },
  {
    industry: 'Hospitality',
    avgTimeSaved: '55hrs/mo',
    avgCostReduction: '40%',
    commonAutomations: [
    'Reservation management',
    'Guest communications',
    'Inventory tracking',
    'Feedback collection'],

    adoptionRate: '62%'
  }];


  const handleLearnMore = (industryId: string) => {
    const industry = industries.find((ind) => ind.id === industryId);
    if (industry) {
      setSelectedIndustry(industry);
      setIsModalOpen(true);
    }
  };

  const handleViewCaseStudy = (caseStudyId: string) => {
    console.log('Viewing case study:', caseStudyId);
  };

  return (
    <>
      <Helmet>
        <title>Industry Solutions - Make Mind Automations</title>
        <meta
          name="description"
          content="Discover tailored automation solutions for your industry. Transform your business with proven workflows that save 40-70 hours monthly." />

        <meta
          name="keywords"
          content="industry automation, business process automation, workflow automation, e-commerce automation, real estate automation" />
          <link
  rel="canonical"
  href="https://www.makemindautomations.tech/industry-solutions"
/>


      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main>
          <IndustryHero
            title="Industry-Specific Automation Solutions"
            description="Discover how businesses in your industry are saving 40-70 hours monthly with tailored automation workflows. Explore proven solutions, real case studies, and calculate your potential ROI."
            stats={heroStats} />


          <section className="py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
                  Choose Your Industry
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Select your industry to explore tailored automation solutions, case studies, and ROI
                  calculators
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {industries.map((industry) =>
                <IndustryCard
                  key={industry.id}
                  industry={industry}
                  onLearnMore={handleLearnMore} />

                )}
              </div>
            </div>
          </section>

          <section className="bg-surface py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
                    Calculate Your ROI
                  </h2>
                  <p className="text-lg text-text-secondary mb-8">
                    See exactly how much time and money you could save with automation tailored to
                    your industry. Our calculator uses real data from businesses like yours.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-full flex-shrink-0 mt-1">
                        <Icon name="Check" size={16} className="text-success" />
                      </div>
                      <div>
                        <h3 className="font-medium text-text-primary mb-1">
                          Industry-Specific Calculations
                        </h3>
                        <p className="text-sm text-text-secondary">
                          Based on real data from businesses in your vertical
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-full flex-shrink-0 mt-1">
                        <Icon name="Check" size={16} className="text-success" />
                      </div>
                      <div>
                        <h3 className="font-medium text-text-primary mb-1">
                          Comprehensive Analysis
                        </h3>
                        <p className="text-sm text-text-secondary">
                          See time saved, cost reduction, and ROI projections
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-full flex-shrink-0 mt-1">
                        <Icon name="Check" size={16} className="text-success" />
                      </div>
                      <div>
                        <h3 className="font-medium text-text-primary mb-1">Instant Results</h3>
                        <p className="text-sm text-text-secondary">
                          Get your personalized automation savings report immediately
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <ROICalculator industryName="Your Industry" />
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
                  Success Stories
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Real transformations from businesses that automated their way to success
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {industries.slice(0, 3).map((industry) =>
                industry.caseStudies.map((caseStudy) =>
                <CaseStudyCard
                  key={caseStudy.id}
                  caseStudy={caseStudy}
                  onViewDetails={handleViewCaseStudy} />

                )
                )}
              </div>
            </div>
          </section>

          <IndustryBenchmarks benchmarks={benchmarks} />

          <CTASection />

          <IndustryDetailModal
            industry={selectedIndustry}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)} />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default IndustrySolutions;